"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /** Tighter spacing + smaller cells for use inside dense popovers. */
  compact?: boolean;
};

function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  compact = false,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(compact ? "p-2" : "p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: compact ? "space-y-2" : "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: cn("font-medium", compact ? "text-xs" : "text-sm"),
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          compact ? "h-6 w-6" : "h-7 w-7",
          "bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: cn(
          "text-muted-foreground rounded-md font-normal",
          compact ? "w-8 text-[0.7rem]" : "w-9 text-[0.8rem]",
        ),
        row: cn("flex w-full", compact ? "mt-1" : "mt-2"),
        // Rows are flex. Hidden leading/trailing days (showOutsideDays={false})
        // render as empty cells; without a matching width they collapse and
        // the 1st of the month always sits under Sunday.
        cell: cn(
          "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
          compact ? "w-8" : "w-9",
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          compact ? "h-8 w-8" : "h-9 w-9",
          "p-0 font-normal aria-selected:opacity-100",
        ),
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground dark:bg-brand-accent dark:text-brand-accent-foreground dark:hover:bg-brand-accent dark:focus:bg-brand-accent",
        day_today: "bg-accent/50 text-accent-foreground",
        day_outside: "text-muted-foreground opacity-50",
        day_disabled: "text-muted-foreground opacity-50",
        // Dark variants need the extra specificity to beat day_selected's
        // dark:bg-brand-accent on in-range days.
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground dark:aria-selected:bg-muted dark:aria-selected:text-foreground dark:aria-selected:hover:bg-muted dark:aria-selected:focus:bg-muted",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="w-4 h-4" />,
        IconRight: () => <ChevronRight className="w-4 h-4" />,
        ...components,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
